import React, { useRef, useContext, useMemo } from 'react';
import { useThree, extend } from 'react-three-fiber';
import { Effects as EffectComposer } from '@react-three/drei/Effects';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { KaleidoShader } from 'three/examples/jsm/shaders/KaleidoShader';
import { GlitchPass } from 'three/examples/jsm/postprocessing/GlitchPass';
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass';
import { RGBShiftShader } from 'three/examples/jsm/shaders/RGBShiftShader';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass';

import { ConfigContext } from './config';

extend({ ShaderPass, GlitchPass, AfterimagePass, UnrealBloomPass, BokehPass });

const needsSwap = true;

const Effects = () => {
  const composer = useRef();
  const { scene, camera } = useThree();
  const { useConfig } = useContext(ConfigContext);
  const enableKaleidoscope = useConfig('kaleidoscope.enabled');
  const kaleidoscopeSides = useConfig('kaleidoscope.sides');
  const kaleidoscopeAngle = useConfig('kaleidoscope.angle');
  const enableShaders = useConfig('shaders.enabled');
  const enableGlitch = useConfig('shaders.glitch');
  const enableAfterimage = useConfig('shaders.afterimage.enabled');
  const afterimageDamp = useConfig('shaders.afterimage.damp');
  const enableColorShift = useConfig('shaders.colorshift.enabled');
  const colorshiftAmount = useConfig('shaders.colorshift.amount');
  const colorshiftAngle = useConfig('shaders.colorshift.angle');
  const enableBloom = useConfig('shaders.bloom.enabled');
  const bloomStrength = useConfig('shaders.bloom.strength');
  const bloomRadius = useConfig('shaders.bloom.radius');
  const bloomThreshold = useConfig('shaders.bloom.threshold');
  const enableBokeh = useConfig('shaders.bokeh.enabled');
  const bokehFocus = useConfig('shaders.bokeh.focus');
  const bokehAperture = useConfig('shaders.bokeh.aperture');

  const bloomArgs = useMemo(() => [undefined, bloomStrength, bloomRadius, bloomThreshold], [
    bloomStrength,
    bloomRadius,
    bloomThreshold,
  ]);
  const bokehArgs = useMemo(() => [scene, camera, { focus: bokehFocus, aperture: bokehAperture / 100 }], [
    camera,
    scene,
    bokehFocus,
    bokehAperture,
  ]);

  return (
    <EffectComposer ref={composer} disableGamma={enableShaders && enableAfterimage}>
      {enableShaders && (
        <>
          {enableGlitch && <glitchPass attachArray="passes" />}
          {enableAfterimage && <afterimagePass attachArray="passes" needsSwap={needsSwap} args={[afterimageDamp]} />}
          {enableColorShift && (
            <shaderPass
              attachArray="passes"
              args={[RGBShiftShader]}
              needsSwap={needsSwap}
              uniforms-amount-value={colorshiftAmount}
              uniforms-angle-value={colorshiftAngle}
            />
          )}
          {enableBloom && <unrealBloomPass attachArray="passes" needsSwap={needsSwap} args={bloomArgs} />}
          {enableBokeh && <bokehPass attachArray="passes" needsSwap={needsSwap} args={bokehArgs} />}
        </>
      )}

      {enableKaleidoscope && (
        <shaderPass
          attachArray="passes"
          args={[KaleidoShader]}
          needsSwap={needsSwap}
          uniforms-angle-value={kaleidoscopeAngle}
          uniforms-sides-value={kaleidoscopeSides}
        />
      )}
    </EffectComposer>
  );
};

export default Effects;
